import React, {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import AdminHeader from '../header/AdminHeader';
import AdminSidebar from '../sidebar/AdminSidebar';
import AdminFooter from "../footer/AdminFooter";
import styles from './AdminLayout.module.css';

interface AdminLayoutProps {
    children: ReactNode;
    className?: string;
    isFirstTempLogin?: boolean;
}

// 상수 정의
const MOBILE_BREAKPOINT = 1024;
const INITIALIZATION_DELAY = 100;
const STORAGE_KEYS = {
    SIDEBAR_LOCKED: 'admin-sidebar-locked'
} as const;

const AdminLayout: React.FC<AdminLayoutProps> = ({
                                                     children,
                                                     className,
                                                     isFirstTempLogin = false,
                                                 }) => {
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
    const [isSidebarLocked, setIsSidebarLocked] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // localStorage 유틸리티 함수들
    const saveToStorage = useCallback((key: string, value: boolean) => {
        if (!isMobile) {
            try {
                localStorage.setItem(key, JSON.stringify(value));
            } catch (error) {
                console.warn('Failed to save to localStorage:', error);
            }
        }
    }, [isMobile]);

    const loadFromStorage = useCallback((key: string): boolean | null => {
        try {
            const saved = localStorage.getItem(key);
            return saved !== null ? JSON.parse(saved) : null;
        } catch (error) {
            console.warn('Failed to load from localStorage:', error);
            return null;
        }
    }, []);

    // 사이드바 상태 동기화 함수
    const syncSidebarState = useCallback(() => {
        if (isMobile) {
            // 모바일에서는 항상 닫힌 상태로 시작
            setIsSidebarExpanded(false);
            setIsSidebarLocked(false);
            return;
        }

        // PC 환경에서 잠금 상태만 복원
        const savedLocked = loadFromStorage(STORAGE_KEYS.SIDEBAR_LOCKED);
        const isLocked = savedLocked === true;

        // 상태를 동시에 설정하여 애니메이션 방지
        setIsSidebarLocked(isLocked);
        setIsSidebarExpanded(isLocked);

    }, [isMobile, loadFromStorage]);

    // 모바일 환경 체크 및 초기화
    const checkMobileAndInitialize = useCallback(() => {
        const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
        const wasMobile = isMobile;

        setIsMobile(mobile);

        // 모바일 상태가 변경되었거나 초기 로드인 경우에만 동기화
        if (mobile !== wasMobile || !isInitialized) {
            // 초기 상태 동기화를 먼저 실행 (애니메이션 없이)
            if (!isInitialized) {
                syncSidebarState();
            }

            // 그 다음에 애니메이션 활성화
            setTimeout(() => {
                if (mobile === wasMobile && isInitialized) {
                    // 리사이즈만 발생한 경우 상태 재동기화
                    syncSidebarState();
                }
                setIsInitialized(true);
            }, INITIALIZATION_DELAY);
        }
    }, [isMobile, isInitialized, syncSidebarState]);

    // 컴포넌트 마운트 시 상태 복원
    useEffect(() => {
        checkMobileAndInitialize();

        // 리사이즈 이벤트 리스너
        const handleResize = () => {
            checkMobileAndInitialize();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [checkMobileAndInitialize]);

    // 페이지 변경 시 상태 동기화
    useEffect(() => {
        if (isInitialized && !isMobile) {
            syncSidebarState();
        }
    }, [window.location.pathname, isInitialized, isMobile, syncSidebarState]);

    // PC 환경: 사이드바 잠금 토글 (로고 클릭)
    const handleSidebarToggle = useCallback(() => {
        if (isMobile) return;

        const newLocked = !isSidebarLocked;

        setIsSidebarLocked(newLocked);
        saveToStorage(STORAGE_KEYS.SIDEBAR_LOCKED, newLocked);

        // 잠금 상태에 따라 확장 상태 결정
        setIsSidebarExpanded(newLocked);
    }, [isMobile, isSidebarLocked, saveToStorage]);

    // 모바일 환경: 헤더 햄버거 메뉴 토글
    const handleMobileToggle = useCallback(() => {
        if (!isMobile) return;

        setIsSidebarExpanded(prev => !prev);
    }, [isMobile]);

    // 모바일에서 메뉴 클릭 시 사이드바 닫기
    const handleMenuClick = useCallback(() => {
        if (isMobile) {
            setIsSidebarExpanded(false);
        }
    }, [isMobile]);

    // PC에서 hover 처리 (잠금되지 않은 경우에만)
    const handleSidebarExpand = useCallback((expanded: boolean) => {
        if (isMobile || isSidebarLocked) return;

        setIsSidebarExpanded(expanded);
    }, [isMobile, isSidebarLocked]);

    // 메인 콘텐츠 클래스명 계산
    const mainContentClassName = useMemo(() => {
        const classes = [styles.mainContent];
        if (isSidebarExpanded) classes.push(styles.expanded);
        if (!isInitialized) classes.push(styles.noTransition);
        if (className) classes.push(className);
        return classes.join(' ');
    }, [isSidebarExpanded, isInitialized, className]);

    return (
        <div className={styles.layout}>
            <AdminSidebar
                className={styles.sidebar}
                onExpand={handleSidebarExpand}
                onToggle={handleSidebarToggle}
                onMenuClick={handleMenuClick}
                isExpanded={isSidebarExpanded}
                isLocked={isSidebarLocked}
                isMobile={isMobile}
                isInitialized={isInitialized}
                isFirstTempLogin={isFirstTempLogin}
            />
            <div className={mainContentClassName}>
                <AdminHeader
                    className={styles.header}
                    isSidebarExpanded={isSidebarExpanded}
                    toggleSidebar={handleMobileToggle}
                />
                <main className={styles.content}>
                    {children}
                </main>
                <AdminFooter className={styles.footer} />
            </div>
        </div>
    );
};

export default AdminLayout;